// Personen-Zeile im Projekt-Kopf (#854): Owner änderbar, Mitglieder sichtbar
// und direkt auswählbar.
//
// Vorher stand dort nur „Owner: <Name>" als Text — der Owner war über die
// Oberfläche gar nicht wechselbar (die Route kann es seit immer: PATCH
// /api/projects/:id mit owner_id, nur Owner/Admin), und die Mitglieder waren
// ausschließlich im Teilen-Dialog zu sehen. Die Frage „wer ist hier zuständig?"
// ließ sich am Projekt also nicht beantworten, ohne einen Dialog zu öffnen.
//
// Mitglied == Zugriff: das ist bewusst KEIN zweites, davon getrenntes
// Zuständigkeits-Feld. Wer hier hinzukommt, wird `project_members` mit der Rolle
// „Bearbeiter" hinzugefügt (identisch zum Teilen-Dialog, der weiterhin die
// Rollen-Feinsteuerung und das Entfernen mit Rückfrage anbietet).
//
// Der Owner steht NICHT in `project_members` (owner_id auf projects) und ist
// deshalb aus Wert und Kandidaten des Mitglieder-Pickers ausgenommen — sonst
// könnte man ihn „als Mitglied entfernen", was nichts tut, oder ihn doppelt
// eintragen.
//
// Mitglied != alle mit Zugriff: die Zeile zeigt die namentliche Freigabe, nicht
// die Space-, Abteilungs-, Organisations-, Admin- oder Gast-Zugriffe. Deshalb
// steht daneben „Wer hat Zugriff?" (AccessOverviewDialog) — die Zeile
// beantwortet „wer ist zuständig", der Dialog „wer kann mitlesen".

import { useMemo, useState } from "preact/hooks";
import { Eye } from "lucide-preact";
import type { ProjectWithCounts } from "@juuplan/shared";
import { useUsers, findUser } from "../store/users";
import { useMembers, addMember, removeMember, refreshMembers } from "../store/members";
import { patchProject } from "../store/project";
import { AccessOverviewDialog } from "./AccessOverviewDialog";
import { AssigneePicker } from "./AssigneePicker";
import { AvatarStack } from "./AvatarStack";
import { UserAvatar } from "./UserAvatar";

interface Props {
  project: ProjectWithCounts;
  /** Owner/Admin — nur sie dürfen Owner wechseln und Mitglieder pflegen. */
  canManage: boolean;
}

export function ProjectPeople(props: Props): preact.JSX.Element {
  const { project, canManage } = props;
  useUsers(); // Namen/Avatare für die Anzeige aufwärmen
  const members = useMembers(project.id);
  const [busy, setBusy] = useState(false);
  const [showAccess, setShowAccess] = useState(false);

  const owner = findUser(project.owner_id);
  const memberIds = useMemo(
    () => members.items.map((m) => m.user.id).filter((id) => id !== project.owner_id),
    [members.items, project.owner_id]
  );

  const setOwner = async (next: number[]): Promise<void> => {
    const nextOwner = next[0];
    if (busy || nextOwner === undefined || nextOwner === project.owner_id) return;
    setBusy(true);
    try {
      // patchProject frischt Detail UND Projektliste selbst auf. Der neue Owner
      // kann vorher Mitglied gewesen sein — dann steht er in beiden Quellen, der
      // Mitglieder-Picker filtert ihn deshalb ueber owner_id heraus.
      await patchProject(project.id, { owner_id: nextOwner });
      await refreshMembers(project.id);
    } catch {
      // api() toastet die Server-Meldung (z. B. fehlende Berechtigung).
    } finally {
      setBusy(false);
    }
  };

  const applyMembers = async (next: number[]): Promise<void> => {
    if (busy) return;
    const wanted = next.filter((id) => id !== project.owner_id);
    const added = wanted.filter((id) => !memberIds.includes(id));
    const removed = memberIds.filter((id) => !wanted.includes(id));
    if (added.length === 0 && removed.length === 0) return;

    setBusy(true);
    try {
      for (const id of added) await addMember(project.id, id, "editor");
      for (const id of removed) await removeMember(project.id, id);
      // member_count im Kopf und die Sidebar-Gruppierung haengen daran.
      await patchProject(project.id, {});
    } catch {
      // api() toastet; der Store liest bei Fehler selbst neu.
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="text-xs text-[color:var(--color-on-surface-variant)] flex flex-wrap items-center gap-x-3 gap-y-2">
      <span class="inline-flex items-center gap-1.5">
        <span>Owner:</span>
        {canManage ? (
          <span class="inline-block w-52">
            <AssigneePicker
              single
              compact
              value={[project.owner_id]}
              onChange={setOwner}
              label="Owner"
            />
          </span>
        ) : (
          <span class="inline-flex items-center gap-1.5">
            <UserAvatar userId={project.owner_id} size={16} />
            <span class="truncate max-w-[180px]">
              {owner?.name ?? `User #${project.owner_id}`}
            </span>
          </span>
        )}
      </span>

      <span class="inline-flex items-center gap-1.5">
        <span>Mitglieder:</span>
        {canManage ? (
          <span class="inline-block w-52">
            <AssigneePicker
              compact
              value={memberIds}
              onChange={applyMembers}
              label="Mitglieder"
              emptyLabel="— Niemand —"
            />
          </span>
        ) : memberIds.length > 0 ? (
          <AvatarStack userIds={memberIds} />
        ) : (
          <span>—</span>
        )}
      </span>

      {/* „Mitglieder" oben zeigt nur die NAMENTLICHE Freigabe. Wer ueber den
          Space, eine Abteilung, die organisationsweite Sichtbarkeit, die
          Administration oder eine Gast-Einladung hereinkommt, steht dort nicht
          — und das sind meist die meisten. Dieser Knopf fuehrt zur
          vollstaendigen Antwort; sichtbar fuer JEDEN mit Projektzugriff, nicht
          nur fuer Verwaltende. */}
      <button
        type="button"
        onClick={() => setShowAccess(true)}
        class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
      >
        <Eye size={14} aria-hidden="true" />
        Wer hat Zugriff?
      </button>

      {showAccess && (
        <AccessOverviewDialog
          subject={{ kind: "project", id: project.id }}
          onClose={() => setShowAccess(false)}
        />
      )}
    </div>
  );
}
